import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { createUseStyles } from "react-jss";
import { withTranslation } from "react-i18next";
import { Permissions } from "../../config";
import DeleteButton from "components/Main/DeleteButton.js";

// material-ui icons
import { Edit, AddAlert, Add, VpnKey } from "@mui/icons-material";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import SnackbarCustom from "components/Snackbar/Snackbar.js";
import SparaLoader from "components/SparaLoader/SparaLoader";
import SparaTitle from "components/SparaTitle/SparaTitle";
import SparaButton from "components/SparaButton/SparaButton";
import SparaFilter from "components/SparaFilter/SparaFilter";

import styles from "assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js";
import stylesTab from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

import {
    getPermissions,
    createUpdatePermission,
    deletePermission,
} from "../../store/actions/acl";
import AddPermission from "./addPermission";

const style = {
    ...styles,
    ...stylesTab,
    stickyFilterContainer: {
        position: 'sticky',
        top: '50px',
        backgroundColor: 'var(--spara-chrome, white)',
        zIndex: 99,
        padding: '8px 16px 16px 16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    listHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '4px 8px 12px 8px',
    },
    listHeaderCount: {
        fontWeight: 'bold',
        fontSize: '16px',
        fontStyle: 'italic',
        color: '#007aaa',
    },
    permissionsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    permissionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid rgba(0,0,0,0.07)',
        backgroundColor: 'var(--spara-surface, #fff)',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 122, 170, 0.1)',
            borderColor: 'rgba(0, 122, 170, 0.25)',
        },
    },
    permissionIconBubble: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #fbeaec 0%, #f8d5da 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#de2c48',
    },
    permissionInfo: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    permissionName: {
        fontWeight: 600,
        fontSize: '0.875rem',
        color: 'var(--spara-text-strong, #2d3748)',
        margin: 0,
        letterSpacing: '0.01em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    permissionIdBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '0.7rem',
        color: 'var(--spara-text-muted, #718096)',
        backgroundColor: 'var(--spara-surface-2, #f7fafc)',
        border: '1px solid var(--spara-border, #e2e8f0)',
        borderRadius: '4px',
        padding: '1px 6px',
        fontFamily: 'monospace',
        flexShrink: 0,
        letterSpacing: '0.02em',
    },
    permissionActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        flexShrink: 0,
    },
    noResults: {
        textAlign: 'center',
        padding: '40px 32px',
        color: 'rgba(0,0,0,0.4)',
        fontSize: '15px',
    },
};
const useStyles = createUseStyles(style);

const PermissionsPage = ({ t }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { permissions, loading } = useSelector((state) => state.acl);
    const { status, message, type } = useSelector((state) => state.acl.notification);
    const [alert, setAlert] = useState(null);
    const [search, setSearch] = useState('');
    const userPermission = JSON.parse(localStorage.getItem("user")).permission;
    const PERMISSION_UPDATE = userPermission.includes(Permissions.PERMISSION_UPDATE);
    const PERMISSION_DELETE = userPermission.includes(Permissions.PERMISSION_DELETE);

    useEffect(() => {
        dispatch(getPermissions());
    }, [dispatch]);

    const onSubmit = (data, update) => {
        dispatch(createUpdatePermission({ update: update, ...data }));
    };

    const hideAlert = () => {
        setAlert(null);
    };

    const htmlAlert = (data = {}) => {
        setAlert(
            <AddPermission
                classes={classes}
                data={data}
                onCancel={() => hideAlert()}
                onSubmit={(data, update) => onSubmit(data, update)}
            />
        );
    };

    const handleSearchChange = (event) => {
        setSearch(event.target.value || '');
    };

    const filteredPermissions = useMemo(() => {
        if (!permissions) return [];
        if (!search) return permissions;
        const searchLower = search.toLowerCase();
        return permissions.filter(
            (p) =>
                p.name.toLowerCase().includes(searchLower) ||
                String(p.id).includes(searchLower)
        );
    }, [permissions, search]);

    return (
        <GridContainer>
            {alert && createPortal(alert, document.body)}
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={type === "success" ? "success" : "warning"}
                message={message}
                open={status}
            />
            <GridItem xs={12}>
                <SparaTitle
                    title={t('menu.permissions')}
                    rightComponent={
                        userPermission.includes(Permissions.PERMISSION_CREATE) && (
                            <SparaButton
                                color="primary"
                                icon={Add}
                                onClick={() => htmlAlert()}
                            >
                                {t('acl.add_perm')}
                            </SparaButton>
                        )
                    }
                />
                <Card style={{ margin: '0px' }}>
                    <div className={classes.stickyFilterContainer}>
                        <SparaFilter
                            filters={{}}
                            onFilterClick={() => {}}
                            onClearAll={() => setSearch('')}
                            search={search}
                            onSearchChange={handleSearchChange}
                            filterGroups={[]}
                            t={t}
                        />
                    </div>
                    <CardBody>
                        {loading ? (
                            <SparaLoader text={t("common.loading")} />
                        ) : filteredPermissions.length === 0 ? (
                            <div className={classes.noResults}>
                                {t('no_results_with_current_filter')}
                            </div>
                        ) : (
                            <>
                                <div className={classes.listHeader}>
                                    <span className={classes.listHeaderCount}>
                                        {`${t('filter.showing')} ${filteredPermissions.length} ${t('filter.of')} ${permissions.length} ${t('menu.permissions').toLowerCase()}`}
                                    </span>
                                </div>
                                <div className={classes.permissionsList}>
                                    {filteredPermissions.map((value) => (
                                        <div key={value.id} className={classes.permissionRow}>
                                            <div className={classes.permissionIconBubble}>
                                                <VpnKey style={{ fontSize: '17px' }} />
                                            </div>
                                            <div className={classes.permissionInfo}>
                                                <p className={classes.permissionName}>
                                                    {value.name}
                                                </p>
                                                <span className={classes.permissionIdBadge}>
                                                    #{value.id}
                                                </span>
                                            </div>
                                            <div className={classes.permissionActions}>
                                                {PERMISSION_UPDATE && (
                                                    <Button
                                                        color="success"
                                                        className={classes.actionButton}
                                                        onClick={() => htmlAlert(value)}
                                                        title={t("edit")}
                                                    >
                                                        <Edit className={classes.icon} />
                                                    </Button>
                                                )}
                                                {PERMISSION_DELETE && (
                                                    <DeleteButton
                                                        key={value.id + "_delete"}
                                                        action={deletePermission({ ...value })}
                                                        setLimit={() => {}}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default withTranslation("", { withRef: true })(PermissionsPage);
